/// #939 — regra pura do painel de negócio: janela do período, pilha de
/// cursores da paginação e rótulo de origem. Fora do componente pra ser
/// testável sem render (Camada 3a).

/// Opções de período. "Tudo (200 mais recentes)" saiu: sem o teto de 200 ela
/// viraria "a base inteira", e o operador tem o "Personalizado" pra isso.
export const PERIOD_OPTIONS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '365', label: 'Últimos 12 meses' },
  { value: 'custom', label: 'Personalizado' },
] as const;

export type PeriodValue = (typeof PERIOD_OPTIONS)[number]['value'];

export interface PeriodRange {
  since?: string;
  until?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/// Janela do período → `since`/`until` ISO pra API.
///
/// Os rolantes ("últimos N dias") contam de agora pra trás. O personalizado
/// recebe datas de calendário (`YYYY-MM-DD` do `<input type="date">`) e as
/// lê no **horário de Brasília**, o fuso em que o painel fala (decisão de
/// 10/08, o mesmo do `BrazilTime` no backend): o fim é INCLUSIVO pro
/// operador ("até 30/09" inclui o dia 30), então vira o início do dia
/// seguinte, que a API trata como limite exclusivo. Offset fixo −03:00 porque
/// o Brasil não tem horário de verão desde 2019.
///
/// `null` = personalizado incompleto ou invertido: o painel não consulta em
/// vez de consultar outra janela.
export function periodRange(
  period: string,
  custom: { from: string; to: string },
  now: number,
): PeriodRange | null {
  if (period === 'custom') {
    const from = brtMidnight(custom.from);
    const to = brtMidnight(custom.to);
    if (from == null || to == null) return null;
    const until = to + DAY_MS;
    if (until <= from) return null;
    return { since: new Date(from).toISOString(), until: new Date(until).toISOString() };
  }
  const days = Number(period);
  if (!Number.isFinite(days) || days <= 0) return null;
  return { since: new Date(now - days * DAY_MS).toISOString() };
}

function brtMidnight(date: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const t = new Date(`${date}T00:00:00-03:00`).getTime();
  return Number.isNaN(t) ? null : t;
}

/// Paginação por keyset só anda pra frente no servidor. O "Anterior" sai de
/// uma pilha no cliente: `cursors[i]` é o cursor que abre a página `i`
/// (`null` na primeira). Trocar filtro volta pra `initialPager()`.
export interface Pager {
  cursors: (string | null)[];
}

export const initialPager = (): Pager => ({ cursors: [null] });

export const pageIndex = (p: Pager): number => p.cursors.length - 1;

export const currentCursor = (p: Pager): string | null => p.cursors[p.cursors.length - 1];

export function nextPage(p: Pager, nextCursor: string | null | undefined): Pager {
  // Sem próxima página o botão já está desabilitado; se mesmo assim chegar
  // aqui, ficar onde está é melhor que empilhar um cursor nulo (que reabriria
  // a primeira página no meio da pilha).
  if (!nextCursor) return p;
  return { cursors: [...p.cursors, nextCursor] };
}

export function prevPage(p: Pager): Pager {
  if (p.cursors.length <= 1) return p;
  return { cursors: p.cursors.slice(0, -1) };
}

const nf = new Intl.NumberFormat('pt-BR');

/// "1–50 de 3.482". O total vem do resumo (mesmo filtro da lista). Sem total
/// ainda (resumo carregando ou com erro), mostra só a faixa.
export function pageRangeLabel(
  index: number,
  pageSize: number,
  itemsOnPage: number,
  total: number | null | undefined,
): string {
  if (itemsOnPage === 0) return total != null ? `0 de ${nf.format(total)}` : '0';
  const first = index * pageSize + 1;
  const last = first + itemsOnPage - 1;
  const range = `${nf.format(first)}–${nf.format(last)}`;
  return total != null ? `${range} de ${nf.format(total)}` : range;
}

/// Origens do filtro. Os três baldes do servidor cobrem tudo: `phone` é o
/// complemento dos outros dois, então Celular + Relógio + Sem /start somam o
/// total (revisão do #1168).
export const SOURCE_OPTIONS = [
  { value: 'phone', label: 'Celular' },
  { value: 'watch', label: 'Relógio' },
  // Curto de propósito: o rótulo longo cortava no select. A explicação
  // ("criada pelo servidor") está no tooltip da etiqueta da linha.
  { value: 'server', label: 'Sem /start' },
] as const;

/// `source` da shell que o servidor cria quando o `/start` nunca chegou (#638).
export const SERVER_AUTOCREATE_SOURCE = 'server_autocreate';

/// Na shell do servidor, tipo e modalidade são o default da entidade
/// (`solo`/`kitesurf`), não declaração do atleta. A tela mostra "-" em vez de
/// "Freeride · Kite" inventados, igual ao CSV, que deixa as colunas vazias.
export function isServerAutocreated(source: string | null | undefined): boolean {
  return source === SERVER_AUTOCREATE_SOURCE;
}

/// Etiqueta de origem da linha. Celular (coluna nula) não ganha etiqueta: é o
/// caso comum e uma etiqueta em toda linha vira ruído. Sessão de relógio conta
/// como qualquer outra desde 14/10/2026, e a shell do servidor também conta:
/// a etiqueta só diz de onde a sessão veio, não que ela está separada.
export function sourceBadge(source: string | null | undefined): string | null {
  switch (source) {
    case 'watch_standalone':
      return 'Wear OS';
    case 'garmin_standalone':
      return 'Garmin';
    case 'apple_watch_standalone':
      return 'Apple Watch';
    case SERVER_AUTOCREATE_SOURCE:
      return 'Sem /start';
    default:
      return null;
  }
}
